import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, Search, Loader2, Award } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const API = `${API_BASE}/api`;

// Category colors
const CATEGORY_COLORS = {
  "Technical Competence": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Regulatory & Compliance Competence": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Digital & Technology Competence": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Critical Thinking & Problem Solving": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Professional Judgement": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Communication & Stakeholder Engagement": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Documentation & Evidence Management": "bg-slate-500/20 text-slate-400 border-slate-500/30",
  "Ethics & Professional Conduct": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Behavioural & Workplace Effectiveness": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Leadership & Strategic Capability": "bg-rose-500/20 text-rose-400 border-rose-500/30"
};

const COMPETENCY_CATEGORIES = [
  "Technical Competence",
  "Regulatory & Compliance Competence",
  "Digital & Technology Competence",
  "Critical Thinking & Problem Solving",
  "Professional Judgement",
  "Communication & Stakeholder Engagement",
  "Documentation & Evidence Management",
  "Ethics & Professional Conduct",
  "Behavioural & Workplace Effectiveness",
  "Leadership & Strategic Capability"
];

/**
 * CompetencySelector - Multi-select competency picker for content
 * 
 * Usage:
 * <CompetencySelector
 *   selectedCompetencies={selectedIds}
 *   onChange={(ids) => setSelectedIds(ids)}
 *   label="Link Competencies"
 * />
 */
export default function CompetencySelector({ 
  selectedCompetencies = [], 
  onChange, 
  label = "Competencies",
  className = ""
}) {
  const { token } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [localSelected, setLocalSelected] = useState([]);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    if (dialogOpen) {
      fetchCompetencies();
      setLocalSelected([...selectedCompetencies]);
    }
  }, [dialogOpen]);
  
  const fetchCompetencies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/competencies/library?status=approved`, { headers });
      setCompetencies(response.data.competencies || []);
    } catch (error) {
      console.error('Failed to fetch competencies:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCompetency = (competencyId) => {
    setLocalSelected(prev => {
      if (prev.includes(competencyId)) {
        return prev.filter(id => id !== competencyId);
      } else {
        return [...prev, competencyId];
      }
    });
  };
  
  const handleSave = () => {
    onChange(localSelected);
    setDialogOpen(false);
  };
  
  const handleRemove = (competencyId) => {
    onChange(selectedCompetencies.filter(id => id !== competencyId));
  };
  
  // Filter competencies
  const filteredCompetencies = competencies.filter(c => {
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || c.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  
  // Get selected competency details
  const selectedDetails = competencies.filter(c => selectedCompetencies.includes(c.competency_id));
  
  return (
    <div className={className}>
      <Label className="text-zinc-300 mb-2 block">{label}</Label>
      
      {/* Selected Competencies Display */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 min-h-[60px]">
        {selectedCompetencies.length === 0 ? (
          <p className="text-zinc-500 text-sm">No competencies selected</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedDetails.map((comp) => (
              <Badge
                key={comp.competency_id}
                variant="outline"
                className={`${CATEGORY_COLORS[comp.category]} pr-1`}
              >
                {comp.name}
                <button
                  onClick={() => handleRemove(comp.competency_id)}
                  className="ml-1 hover:bg-white/10 rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {/* Show count for items not yet loaded */}
            {selectedCompetencies.length > selectedDetails.length && (
              <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                +{selectedCompetencies.length - selectedDetails.length} more
              </Badge>
            )}
          </div>
        )}
      </div>
      
      <Button
        type="button"
        variant="outline"
        onClick={() => setDialogOpen(true)}
        className="mt-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
      >
        <Award className="h-4 w-4 mr-2" />
        Select Competencies
      </Button>
      
      {/* Selection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Competencies</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Link competencies to this content. Selected: {localSelected.length}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex items-center gap-3 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search competencies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-56 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                <SelectItem value="all" className="text-white">All Categories</SelectItem>
                {COMPETENCY_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-white text-sm">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Competencies List */}
          <div className="overflow-y-auto max-h-[400px] border border-zinc-800 rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
              </div>
            ) : filteredCompetencies.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No approved competencies found
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {filteredCompetencies.map((comp) => {
                  const isSelected = localSelected.includes(comp.competency_id);
                  return (
                    <div
                      key={comp.competency_id}
                      onClick={() => toggleCompetency(comp.competency_id)}
                      className={`p-3 cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-teal-500/10 border-l-2 border-l-teal-500' 
                          : 'hover:bg-zinc-800/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 ${
                          isSelected 
                            ? 'bg-teal-500 border-teal-500' 
                            : 'border-zinc-600'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{comp.name}</span>
                            <Badge className={`text-xs ${CATEGORY_COLORS[comp.category]}`}>
                              {comp.category?.split(' ')[0]}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-400 line-clamp-1">{comp.definition}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-teal-500 hover:bg-teal-600">
              Save Selection ({localSelected.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
